import { useEffect, useRef, useState } from 'react';
import {
  DEVICE_ICONS,
  STAGE,
  cameraOverlays,
  channelByKey,
  clockOverlay,
  decodeDevices,
  formatValue,
  iconOverlays,
  legendOverlays,
  levelOverlays,
  levelPercent,
  sensorOverlays,
  stageBox,
} from '@ccs/core';
import type { Sample } from '../api';
import { formatClock } from '../../../shared/format';

const SENSOR_BG: Record<string, string> = {
  T: '#F0B0B1',
  F: '#B0DDC1',
  L: '#B0DDC1',
  P: '#AAD3F5',
};

/** 温度/流量/液位/压力按面板控件名分组；A4_* 通道不能按 Key 首字母判断。 */
function sensorGroup(control: string, key: string): string {
  const matched = /^label_([TFLP])_/.exec(control);
  if (matched) return matched[1];
  return key ? key.charAt(0).toUpperCase() : '';
}

type Props = {
  sample: Sample | null;
  zoom: number;
  selected: string | null;
  onSelect: (channelKey: string) => void;
};

export default function PidStage({ sample, zoom, selected, onSelect }: Props) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const apply = (value: number) => {
      const next = Math.max(0, Math.round(value));
      setWidth((prev) => (prev === next ? prev : next));
    };
    const observer = new ResizeObserver((entries) => {
      apply(entries[0].contentRect.width);
    });
    observer.observe(el);
    apply(el.clientWidth);
    return () => observer.disconnect();
  }, []);

  const scale = (width ? width / STAGE.width : 1) * zoom;
  const values = sample?.values ?? {};
  const devices = decodeDevices(values.dev0, values.dev1);

  return (
    <div className="pid-viewport" ref={viewportRef}>
      <div className="pid-spacer" style={{ width: STAGE.width * scale, height: STAGE.height * scale }}>
        <div className="pid-stage" style={{ transform: `scale(${scale})` }}>
          <img
            className="pid-base"
            src="/assets/pid/pid-2400.webp"
            srcSet="/assets/pid/pid-2400.webp 1x, /assets/pid/pid-6000.webp 2x"
            alt="CCS 系统流程图"
            decoding="async"
            draggable={false}
          />

          {sensorOverlays.map((o) => {
            const key = o.channel_key ?? '';
            const channel = key ? channelByKey.get(key) : undefined;
            const text = formatValue(values[key], channel);
            const bg = SENSOR_BG[sensorGroup(o.control, key)] ?? '#E2E8F0';
            const isDisabled = channel ? !channel.enabled : false;
            const title = [
              channel?.name ?? o.control,
              channel?.unit ?? '',
              isDisabled ? '压缩机子系统未启用' : '',
            ]
              .filter(Boolean)
              .join(' · ');
            // 字号必须稳定：按当前数值长度切换字号会让测点每秒抖动/重影。
            const fontSize = 11;
            return (
              <div
                key={o.control}
                className={`pid-label${isDisabled ? ' disabled' : ''}`}
                style={{
                  ...stageBox(o),
                  background: bg,
                  fontSize,
                  ...(selected === key
                    ? { outline: '2px solid rgba(15,118,110,.9)', outlineOffset: 1 }
                    : {}),
                }}
                title={title}
                onClick={() => key && onSelect(key)}
              >
                {text}
              </div>
            );
          })}

          {levelOverlays.map((o) => {
            const key = o.channel_key ?? '';
            const channel = key ? channelByKey.get(key) : undefined;
            const percent = levelPercent(values[key], channel);
            return (
              <div
                key={o.control}
                className="pid-gauge"
                style={stageBox(o)}
                title={`${channel?.name ?? o.control} · ${percent === null ? '--' : percent.toFixed(0)}%`}
              >
                <i style={{ height: `${percent ?? 0}%` }} />
              </div>
            );
          })}

          {iconOverlays.map((o) => {
            const icon = DEVICE_ICONS[o.control];
            if (!icon || !devices[o.control]) return null;
            return (
              <img
                key={o.control}
                className="pid-device"
                style={stageBox(o)}
                src={`/assets/icons/${icon.image}`}
                alt={icon.label}
                title={icon.label}
              />
            );
          })}

          {cameraOverlays.map((o) => (
            <div key={o.control} className="pid-camera" style={stageBox(o)}>
              <span>
                摄像头未接入
                <br />
                <span style={{ fontSize: 10, opacity: 0.8 }}>第二期</span>
              </span>
            </div>
          ))}

          {legendOverlays.map((o) => (
            <div
              key={o.control}
              className="pid-legend"
              style={{ ...stageBox(o), display: 'flex', alignItems: 'center' }}
            >
              {o.text_static}
            </div>
          ))}

          {clockOverlay ? (
            <div
              className="pid-clock"
              style={{
                ...stageBox(clockOverlay),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              {formatClock(sample?.ts)}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}